import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import type { EventCourtPublic, EventVenuePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

export default function MatchEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [eventCourtId, setEventCourtId] = useState("");
  const [eventVenueId, setEventVenueId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [eventId, setEventId] = useState("");
  const [eventName, setEventName] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [schoolName, setSchoolName] = useState("");

  const [eventCourts, setEventCourts] = useState<EventCourtPublic[]>([]);
  const [eventVenues, setEventVenues] = useState<EventVenuePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getMatch(id)
      .then((res) => {
        const m = res.match;
        setName(m.name);
        setEventCourtId(m.event_court_id ?? "");
        setEventVenueId(m.event_venue_id ?? "");
        setStartsAt(toDatetimeLocal(m.starts_at));
        setEndsAt(toDatetimeLocal(m.ends_at));
        setAgeGroup(m.age_group ?? "");
        setEventId(m.event_id);
        setEventName(m.event_name);
        return Promise.all([api.getEvent(m.event_id), api.listEventVenues(m.event_id)]);
      })
      .then(([eventRes, venuesRes]) => {
        setSchoolId(eventRes.event.school_id);
        setSchoolName(eventRes.event.school_name);
        setEventCourts(eventRes.event.courts);
        setEventVenues(venuesRes.event_venues);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load match",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);

    try {
      await api.updateMatch(id, {
        name,
        event_court_id: eventCourtId || null,
        event_venue_id: eventVenueId || null,
        starts_at: new Date(startsAt).toISOString(),
        age_group: ageGroup || null,
      });
      navigate(`/matches/${id}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError ? err.message : "Failed to update match",
      );
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="match-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="match-edit" data-state={saving ? "submitting" : error ? "error" : "ready"}>
      <div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to={`/schools/${schoolId}`} className="hover:underline">
            {schoolName || "School"}
          </Link>
          <span>/</span>
          <Link
            to={`/schools/${schoolId}/events`}
            className="hover:underline"
          >
            Events
          </Link>
          <span>/</span>
          <Link to={`/events/${eventId}`} className="hover:underline">
            {eventName}
          </Link>
          <span>/</span>
          <Link to={`/matches/${id}`} className="hover:underline">
            {name}
          </Link>
          <span>/</span>
          <span>Edit</span>
        </div>
        <h1 className="text-2xl font-semibold">Edit Match</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex max-w-md flex-col gap-4" data-region="form" data-form="match-edit">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}

        <div className="flex flex-col gap-2">
          <Label htmlFor="name">Match Name</Label>
          <Input
            id="name"
            data-field="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="court_id">Court</Label>
          <select
            id="court_id"
            data-field="court"
            title="Court"
            value={eventCourtId}
            onChange={(e) => setEventCourtId(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            required
          >
            <option value="">Select a court...</option>
            {eventCourts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.is_adhoc ? " (ad-hoc)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="event_venue_id">Venue (for directions)</Label>
          <select
            id="event_venue_id"
            data-field="event-venue"
            title="Venue"
            value={eventVenueId}
            onChange={(e) => setEventVenueId(e.target.value)}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Use event primary venue</option>
            {eventVenues.map((v) => (
              <option key={v.id} value={v.id}>
                {v.display_name}{v.is_primary ? " (primary)" : ""}
              </option>
            ))}
          </select>
          {eventVenues.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No venues configured for this event. Add one on the event edit page.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="starts_at">Start Time</Label>
          <Input
            id="starts_at"
            data-field="starts-at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="ends_at">End Time</Label>
          <Input
            id="ends_at"
            data-field="ends-at"
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="age_group">Age Group (optional)</Label>
          <Input
            id="age_group"
            data-field="age-group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            placeholder="e.g. U14, 1st XV, Open"
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" data-action="submit" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-action="cancel"
            onClick={() => navigate(`/matches/${id}`)}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
